import { FC, useMemo } from 'react';
import { DailyPoint, Platform } from '../mock/types';
import { fmtCurrency, fmtDayLabel, fmtDecimal, fmtInt } from '../utils';

export type ChartMetric = 'impression' | 'click' | 'costo' | 'ctr';

type TrendChartProps = {
  daily: DailyPoint[];
  metric: ChartMetric;
  visiblePlatforms: Platform[];
};

const PAD_L = 48;
const PAD_R = 12;
const PAD_T = 18;
const BOTTOM_Y = 218;
const VBOX_W = 720;
const VBOX_H = 260;

const niceCeil = (v: number): number => {
  if (v <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(v)));
  const f = v / exp;
  let nf = 1;
  if (f <= 1) nf = 1;
  else if (f <= 2) nf = 2;
  else if (f <= 2.5) nf = 2.5;
  else if (f <= 5) nf = 5;
  else nf = 10;
  return nf * exp;
};

export const TrendChart: FC<TrendChartProps> = ({ daily, metric, visiblePlatforms }) => {
  const { dates, googleSeries, metaSeries, maxY, ticks, fmtY } = useMemo(() => {
    const byDate = new Map<string, { google?: DailyPoint; meta?: DailyPoint }>();
    for (const d of daily) {
      const e = byDate.get(d.date) ?? {};
      if (d.platform === 'google') e.google = d;
      if (d.platform === 'meta') e.meta = d;
      byDate.set(d.date, e);
    }
    const dates = Array.from(byDate.keys()).sort();

    const valueFor = (p: DailyPoint | undefined): number => {
      if (!p) return 0;
      switch (metric) {
        case 'impression':
          return p.impressions;
        case 'click':
          return p.clicks;
        case 'costo':
          return p.cost;
        case 'ctr':
          return p.impressions > 0 ? (p.clicks / p.impressions) * 100 : 0;
      }
    };

    const googleSeries = dates.map((d) => valueFor(byDate.get(d)?.google));
    const metaSeries = dates.map((d) => valueFor(byDate.get(d)?.meta));

    let allVals: number[] = [];
    if (visiblePlatforms.includes('google')) allVals = allVals.concat(googleSeries);
    if (visiblePlatforms.includes('meta')) allVals = allVals.concat(metaSeries);
    const peak = Math.max(0, ...allVals);
    const maxY = niceCeil(peak * 1.05) || 1;

    const ticks = [maxY, maxY * 0.75, maxY * 0.5, maxY * 0.25, 0];
    let fmtY: (v: number) => string;
    if (metric === 'impression' || metric === 'click') {
      fmtY = (v) => (v >= 1000 ? `${(v / 1000).toLocaleString('it-IT', { maximumFractionDigits: 0 })}k` : fmtInt(v));
    } else if (metric === 'costo') {
      fmtY = (v) => fmtCurrency(v);
    } else {
      fmtY = (v) => fmtDecimal(v) + '%';
    }

    return { dates, googleSeries, metaSeries, maxY, ticks, fmtY };
  }, [daily, metric, visiblePlatforms]);

  const xFor = (i: number): number => {
    if (dates.length <= 1) return PAD_L + (VBOX_W - PAD_L - PAD_R) / 2;
    return PAD_L + (i * (VBOX_W - PAD_L - PAD_R)) / (dates.length - 1);
  };
  const yFor = (v: number): number => PAD_T + (1 - v / maxY) * (BOTTOM_Y - PAD_T);

  const buildLine = (arr: number[]): string => (arr.length === 0 ? '' : arr.map((v, i) => `${i === 0 ? 'M' : 'L'}${xFor(i)},${yFor(v)}`).join(' '));
  const buildArea = (arr: number[]): string => {
    if (arr.length === 0) return '';
    const line = buildLine(arr);
    return `${line} L${xFor(arr.length - 1)},${BOTTOM_Y} L${xFor(0)},${BOTTOM_Y} Z`;
  };

  const showGoogle = visiblePlatforms.includes('google');
  const showMeta = visiblePlatforms.includes('meta');

  // Tick to text positions
  const gridYs = [PAD_T, PAD_T + (BOTTOM_Y - PAD_T) * 0.25, PAD_T + (BOTTOM_Y - PAD_T) * 0.5, PAD_T + (BOTTOM_Y - PAD_T) * 0.75, BOTTOM_Y];

  // X axis labels: thin them out if many dates
  const xLabelStep = Math.max(1, Math.ceil(dates.length / 7));

  return (
    <svg viewBox={`0 0 ${VBOX_W} ${VBOX_H}`} width='100%' style={{ display: 'block' }}>
      <defs>
        <linearGradient id='cp-trend-g' x1='0' x2='0' y1='0' y2='1'>
          <stop offset='0' stopColor='#1452E6' stopOpacity={0.18} />
          <stop offset='1' stopColor='#1452E6' stopOpacity={0} />
        </linearGradient>
        <linearGradient id='cp-trend-m' x1='0' x2='0' y1='0' y2='1'>
          <stop offset='0' stopColor='#0B1733' stopOpacity={0.14} />
          <stop offset='1' stopColor='#0B1733' stopOpacity={0} />
        </linearGradient>
      </defs>
      <g fontFamily='Inter var, Geist, system-ui' fontSize={11} fill='#9CA3B1'>
        {ticks.map((t, i) => (
          <text key={i} x={PAD_L - 10} y={gridYs[i]! + 4} textAnchor='end'>
            {fmtY(t)}
          </text>
        ))}
      </g>
      <g stroke='#EFF1F5' strokeWidth={1}>
        {gridYs.map((y, i) => (
          <line key={i} x1={PAD_L} y1={y} x2={VBOX_W - PAD_R} y2={y} />
        ))}
      </g>
      {showGoogle ? (
        <>
          <path d={buildArea(googleSeries)} fill='url(#cp-trend-g)' />
          <path d={buildLine(googleSeries)} fill='none' stroke='#1452E6' strokeWidth={2.4} strokeLinecap='round' strokeLinejoin='round' />
        </>
      ) : null}
      {showMeta ? (
        <>
          <path d={buildArea(metaSeries)} fill='url(#cp-trend-m)' />
          <path d={buildLine(metaSeries)} fill='none' stroke='#0B1733' strokeWidth={2.4} strokeDasharray='5 4' strokeLinecap='round' strokeLinejoin='round' />
        </>
      ) : null}
      <g fontFamily='Inter var, Geist, system-ui' fontSize={11} fill='#7A8597'>
        {dates.map((d, i) => {
          if (i % xLabelStep !== 0 && i !== dates.length - 1) return null;
          return (
            <text key={d} x={xFor(i)} y={242} textAnchor='middle'>
              {fmtDayLabel(d)}
            </text>
          );
        })}
      </g>
    </svg>
  );
};
